// src/pages/BillingDashboard.jsx
import BillDetailsModal from "@/components/clienti/BillDetailsModal";
import ConfirmationModal from "@/components/clienti/ConfirmationModal";
import ResultModal from "@/components/clienti/ResultModal";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from '@/lib/supabase'; // Import supabase directly
import {
  BarChart3,
  Calendar,
  CreditCard,
  Download,
  FileText,
  Filter,
  Search
} from "lucide-react";
import { useEffect, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';

export default function BillingDashboard({ 
  isMobile = false
}) {
  // Get admin status from auth context
  const { isAdmin } = useAuth();
  // State management
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedCustomer, setSelectedCustomer] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [revenueData, setRevenueData] = useState([]);
  const [customerBills, setCustomerBills] = useState([]);
  const [clienti, setClienti] = useState([]); // Load clients internally
  // State for bill details modal
  const [showBillDetails, setShowBillDetails] = useState(false);
  const [selectedBillDetails, setSelectedBillDetails] = useState(null);

    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [showResultModal, setShowResultModal] = useState(false);
    const [resultData, setResultData] = useState({ success: false, data: {} });
    const [dashboardStats, setDashboardStats] = useState({
    totalRevenue: 0,
    activeCustomers: 0,
    totalParkedCars: 0,
    monthlyGrowth: 0
    });

  // Helper functions for month handling
  const getMonthName = (monthNumber) => {
    const months = ['Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu', 'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic'];
    return months[monthNumber - 1];
  };

  const getMonthNumber = (monthName) => {
    const months = ['Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu', 'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic'];
    return months.indexOf(monthName) + 1;
  };

  // Load real data from database
  const loadDashboardData = async () => {
    try {
      setIsLoading(true);
      
      // Load clients data first
      const { data: clientsData, error: clientsError } = await supabase
        .from('clienti')
        .select('*')
        .order('nome', { ascending: true });

      if (clientsError) throw clientsError;
      setClienti(clientsData || []);
      
      // Load monthly bills data - for the customer list, we need ALL bills
      // For the revenue chart, we filter by customer if selected
      let billsQuery = supabase
        .from('fatture_mensili')
        .select(`
          id,
          cliente_id,
          anno,
          mese,
          importo_totale,
          numero_auto,
          stato,
          created_at,
          clienti (
            nome,
            cognome,
            pricing_model,
            daily_rate,
            monthly_rate
          )
        `)
        .order('created_at', { ascending: false });

      // For revenue chart: filter by customer only if selected
      let revenueQuery = supabase
        .from('fatture_mensili')
        .select(`
          anno,
          mese,
          importo_totale
        `);

      if (selectedCustomer) {
        revenueQuery = revenueQuery.eq('cliente_id', selectedCustomer);
      }

      const { data: billsData, error: billsError } = await billsQuery;
      const { data: revenueRawData, error: revenueError } = await revenueQuery;

      if (billsError) throw billsError;
      if (revenueError) throw revenueError;

      console.log('Bills data loaded:', billsData); // Debug log

      // If no bills exist, show empty state
      if (!billsData || billsData.length === 0) {
        setCustomerBills([]);
        setRevenueData([]);
        setDashboardStats({
          totalRevenue: 0,
          activeCustomers: 0,
          totalParkedCars: 0,
          monthlyGrowth: 0
        });
        return;
      }

      // Transform bills data for customer billing table - keep ALL bills for filtering
      const customerBillsData = billsData.map(bill => ({
        id: bill.cliente_id,
        customer: `${bill.clienti.nome} ${bill.clienti.cognome}`,
        pricingModel: bill.clienti.pricing_model,
        rate: bill.clienti.pricing_model === 'per_day' ? bill.clienti.daily_rate : bill.clienti.monthly_rate,
        activeCars: bill.numero_auto,
        lastBillGenerated: bill.created_at,
        monthlyAmount: parseFloat(bill.importo_totale),
        status: bill.stato === 'generated' ? 'generated' : 'pending',
        anno: bill.anno,
        mese: bill.mese
      }));

      // DON'T remove duplicates here - we need all bills for period filtering
      setCustomerBills(customerBillsData);

      // Calculate revenue data for chart using the filtered revenue data
      const revenueByMonth = (revenueRawData || []).reduce((acc, bill) => {
        const key = `${bill.anno}-${bill.mese}`;
        if (!acc[key]) {
          acc[key] = {
            month: getMonthName(bill.mese),
            revenue: 0,
            year: bill.anno
          };
        }
        acc[key].revenue += parseFloat(bill.importo_totale);
        return acc;
      }, {});

      const revenueChartData = Object.values(revenueByMonth)
        .sort((a, b) => {
          if (a.year !== b.year) return a.year - b.year;
          return getMonthNumber(a.month) - getMonthNumber(b.month);
        })
        .slice(-12); // Last 12 months

      setRevenueData(revenueChartData);

      // Calculate dashboard stats for selected period
      const currentMonthBills = billsData.filter(bill => 
        bill.anno === selectedYear && bill.mese === selectedMonth
      );
      
      const totalRevenue = currentMonthBills.reduce((sum, bill) => sum + parseFloat(bill.importo_totale), 0);
      const activeCustomers = new Set(currentMonthBills.map(bill => bill.cliente_id)).size;
      const totalParkedCars = currentMonthBills.reduce((sum, bill) => sum + bill.numero_auto, 0);

      // Calculate growth (compare with previous month)
      const prevMonth = selectedMonth === 1 ? 12 : selectedMonth - 1;
      const prevYear = selectedMonth === 1 ? selectedYear - 1 : selectedYear;
      const prevMonthBills = billsData.filter(bill => 
        bill.anno === prevYear && bill.mese === prevMonth
      );
      const prevRevenue = prevMonthBills.reduce((sum, bill) => sum + parseFloat(bill.importo_totale), 0);
      const monthlyGrowth = prevRevenue > 0 ? ((totalRevenue - prevRevenue) / prevRevenue * 100) : 0;

      setDashboardStats({
        totalRevenue,
        activeCustomers,
        totalParkedCars,
        monthlyGrowth
      });

    } catch (error) {
      console.error('Error loading dashboard data:', error);
      // On error, show empty state
      setCustomerBills([]);
      setRevenueData([]);
      setClienti([]);
      setDashboardStats({
        totalRevenue: 0,
        activeCustomers: 0,
        totalParkedCars: 0,
        monthlyGrowth: 0
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Filter data based on selected customer  
  const getFilteredRevenueData = () => {
    if (!selectedCustomer) {
      return revenueData; // Show all data
    }

    // We need to re-query the bills data filtered by customer
    // For now, let's filter from the loaded bills and recalculate
    const filteredBills = customerBills.filter(bill => bill.id === selectedCustomer);
    
    if (filteredBills.length === 0) {
      return []; // No data for this customer
    }

    // We need to get all bills for this customer, not just the unique ones
    // This is a limitation - we should reload data filtered by customer
    // For now, return empty array to show the issue
    return [];
  };

  const filteredRevenueData = getFilteredRevenueData();

  // Filter customers based on search term AND selected month/year
  const filteredCustomers = customerBills.filter(customer => {
    // Filter by search term
    const matchesSearch = customer.customer.toLowerCase().includes(searchTerm.toLowerCase());
    
    // Filter by selected month/year (only show customers with bills for this period)
    const matchesPeriod = customer.anno === selectedYear && customer.mese === selectedMonth;
    
    return matchesSearch && matchesPeriod;
  });

  // Generate months array for selector
  const months = [
    { value: 1, label: "Gennaio" },
    { value: 2, label: "Febbraio" },
    { value: 3, label: "Marzo" },
    { value: 4, label: "Aprile" },
    { value: 5, label: "Maggio" },
    { value: 6, label: "Giugno" },
    { value: 7, label: "Luglio" },
    { value: 8, label: "Agosto" },
    { value: 9, label: "Settembre" },
    { value: 10, label: "Ottobre" },
    { value: 11, label: "Novembre" },
    { value: 12, label: "Dicembre" }
  ];

  // Generate years array (current year and previous 2 years)
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 3 }, (_, i) => currentYear - i);

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('it-IT', { 
      style: 'currency', 
      currency: 'EUR' 
    }).format(amount);
  };

  // Get pricing model display text
  const getPricingModelText = (model, rate) => {
    switch (model) {
      case 'per_day':
        return `${formatCurrency(rate)}/giorno`;
      case 'monthly_flat':
        return `${formatCurrency(rate)}/mese`;
      case 'no_cost':
        return 'Gratuito';
      default:
        return 'N/A';
    }
  };

  // Handle PDF generation
  const handleGeneratePDF = async (customerId, month, year) => {
    try {
      setIsLoading(true);
      
      console.log('Calling get_bill_details with:', { customerId, month, year }); // Debug log
      
      // Get detailed bill data for PDF generation
      const { data: billDetails, error } = await supabase
        .rpc('get_bill_details', {
          customer_id: customerId,
          bill_year: year,
          bill_month: month
        });

      console.log('Function result:', { billDetails, error }); // Debug log

      if (error) {
        console.error('Error fetching bill details:', error);
        alert(`Errore nel recupero dei dettagli della fattura: ${error.message}`);
        return;
      }

      if (!billDetails || billDetails.length === 0) {
        alert('Nessuna fattura trovata per questo cliente e periodo');
        return;
      }

      // Generate PDF
      await generateBillPDF(billDetails[0]);
      
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert(`Errore nella generazione del PDF: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle bill generation for selected month/year
    // Handle bill generation confirmation
    const handleGenerateMonthlyBills = () => {
    setShowConfirmModal(true);
    };

    // Execute bill generation after confirmation
    const executeGenerateMonthlyBills = async () => {
    try {
        setShowConfirmModal(false);
        setIsLoading(true);
        
        console.log(`Generating bills for ${selectedMonth}/${selectedYear}`);
        
        // Call the SQL function to generate bills
        const { data: result, error } = await supabase
        .rpc('generate_monthly_bills_with_archive', {
            bill_year: selectedYear,
            bill_month: selectedMonth
        });

        if (error) {
        console.error('Error generating bills:', error);
        setResultData({
            success: false,
            data: {
            message: `Errore nella generazione delle fatture: ${error.message}`,
            details: error.details || error.hint
            }
        });
        setShowResultModal(true);
        return;
        }

        console.log('Bills generation result:', result);
        
        // Show success result modal
        if (result && result.length > 0) {
        const { bills_created, total_revenue, result_message } = result[0];
        setResultData({
            success: true,
            data: {
            bills_created: bills_created,
            total_revenue: parseFloat(total_revenue),
            period: `${months.find(m => m.value === selectedMonth)?.label} ${selectedYear}`,
            message: result_message
            }
        });
        } else {
        setResultData({
            success: true,
            data: {
            period: `${months.find(m => m.value === selectedMonth)?.label} ${selectedYear}`,
            message: 'Fatture generate con successo!'
            }
        });
        }
        
        setShowResultModal(true);
        
        // Reload dashboard data to show the new bills
        loadDashboardData();
        
    } catch (error) {
        console.error('Error generating monthly bills:', error);
        setResultData({
        success: false,
        data: {
            message: `Errore imprevisto durante la generazione delle fatture: ${error.message}`,
            details: 'Riprova o contatta l\'assistenza tecnica.'
        }
        });
        setShowResultModal(true);
    } finally {
        setIsLoading(false);
    }
    };

  // PDF Generation function using jsPDF
  const generateBillPDF = async (billData) => {
    // Dynamic import of jsPDF
    const { jsPDF } = await import('jspdf');
    
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;
    
    // Add logo (square aspect ratio)
    try {
      const logoImg = new Image();
      logoImg.src = '/logo.png';
      await new Promise((resolve, reject) => {
        logoImg.onload = resolve;
        logoImg.onerror = reject;
      });
      
      // Add logo to PDF with square dimensions (30x30)
      doc.addImage(logoImg, 'PNG', 15, 15, 30, 30);
    } catch (error) {
      console.warn('Could not load logo:', error);
    }
    
    // Company header
    doc.setFontSize(20);
    doc.setTextColor(0, 0, 0);
    doc.text('FATTURA PARCHEGGIO', pageWidth / 2, 25, { align: 'center' });
    
    // Bill period and number
    doc.setFontSize(12);
    doc.text(`Periodo: ${billData.mese}/${billData.anno}`, pageWidth / 2, 40, { align: 'center' });
    doc.text(`Fattura N. ${billData.anno}${String(billData.mese).padStart(2, '0')}-${billData.cliente_cognome.toUpperCase()}`, pageWidth / 2, 50, { align: 'center' });
    
    // Customer details section
    doc.setFontSize(14);
    doc.setTextColor(0, 0, 0);
    doc.text('DATI CLIENTE:', 15, 70);
    
    doc.setFontSize(11);
    let yPos = 82;
    doc.text(`Nome: ${billData.cliente_nome} ${billData.cliente_cognome}`, 15, yPos);
    yPos += 8;
    
    if (billData.cliente_email) {
      doc.text(`Email: ${billData.cliente_email}`, 15, yPos);
      yPos += 8;
    }
    if (billData.cliente_telefono) {
      doc.text(`Telefono: ${billData.cliente_telefono}`, 15, yPos);
      yPos += 8;
    }
    
    yPos += 10;
    
    // Pricing model section
    doc.setFontSize(14);
    doc.text('TARIFFAZIONE:', 15, yPos);
    yPos += 12;
    
    doc.setFontSize(11);
    let pricingText = '';
    if (billData.pricing_model === 'per_day') {
      pricingText = `Tariffa giornaliera: €${parseFloat(billData.daily_rate).toFixed(2)}`;
    } else if (billData.pricing_model === 'monthly_flat') {
      pricingText = `Tariffa mensile fissa: €${parseFloat(billData.monthly_rate).toFixed(2)}`;
    }
    doc.text(pricingText, 15, yPos);
    yPos += 8;
    
    doc.text(`Numero auto parcheggiate: ${billData.numero_auto}`, 15, yPos);
    yPos += 15;
    
    // Car details section
    doc.setFontSize(14);
    doc.text('DETTAGLIO VEICOLI:', 15, yPos);
    yPos += 12;
    
    // Table headers
    doc.setFontSize(10);
    doc.setFont(undefined, 'bold');
    doc.text('TARGA', 15, yPos);
    doc.text('TELAIO', 50, yPos);
    doc.text('MARCA/MODELLO', 100, yPos);
    doc.text('DAL', 140, yPos);
    doc.text('AL', 170, yPos);
    yPos += 8;
    
    // Draw header line
    doc.line(15, yPos - 2, pageWidth - 15, yPos - 2);
    yPos += 5;
    
    // Car details
    doc.setFont(undefined, 'normal');
    const cars = billData.car_details || [];
    
    cars.forEach((car, index) => {
      // Check if we need a new page
      if (yPos > pageHeight - 40) {
        doc.addPage();
        yPos = 30;
        
        // Repeat headers on new page
        doc.setFont(undefined, 'bold');
        doc.text('TARGA', 15, yPos);
        doc.text('TELAIO', 50, yPos);
        doc.text('MARCA/MODELLO', 100, yPos);
        doc.text('DAL', 140, yPos);
        doc.text('AL', 170, yPos);
        yPos += 8;
        doc.line(15, yPos - 2, pageWidth - 15, yPos - 2);
        yPos += 5;
        doc.setFont(undefined, 'normal');
      }
      
      const targa = car.targa || 'N/A';
      const telaio = car.numero_telaio || 'N/A';
      const marcaModello = `${car.marca || ''} ${car.modello || ''}`.trim() || 'N/A';
      const dataIngresso = car.data_ingresso ? new Date(car.data_ingresso).toLocaleDateString('it-IT') : 'N/A';
      const dataUscita = car.data_uscita ? new Date(car.data_uscita).toLocaleDateString('it-IT') : 'In corso';
      
      doc.text(targa, 15, yPos);
      doc.text(telaio.length > 12 ? telaio.substring(0, 12) + '...' : telaio, 50, yPos);
      doc.text(marcaModello.length > 20 ? marcaModello.substring(0, 20) + '...' : marcaModello, 100, yPos);
      doc.text(dataIngresso, 140, yPos);
      doc.text(dataUscita, 170, yPos);
      
      yPos += 12;
    });
    
    // Add some space
    yPos += 15;
    
    // Check if we need a new page for totals
    if (yPos > pageHeight - 50) {
      doc.addPage();
      yPos = 30;
    }
    
    // Total section
    doc.setFontSize(16);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(0, 0, 0);
    doc.text('TOTALE DA PAGARE:', 15, yPos);
    
    // Total amount in a box
    const totalText = `€ ${parseFloat(billData.importo_totale).toFixed(2)}`;
    const totalWidth = doc.getTextWidth(totalText);
    doc.setFillColor(240, 240, 240);
    doc.rect(pageWidth - 15 - totalWidth - 10, yPos - 10, totalWidth + 10, 15, 'F');
    doc.setTextColor(200, 0, 0);
    doc.text(totalText, pageWidth - 15 - totalWidth - 5, yPos);
    
    yPos += 20;
    
    // Payment terms or notes
    doc.setFontSize(10);
    doc.setTextColor(60, 60, 60);
    doc.setFont(undefined, 'normal');
    doc.text('Pagamento entro 30 giorni dalla data di emissione.', 15, yPos);
    yPos += 8;
    doc.text('Per informazioni contattare l\'amministrazione del parcheggio.', 15, yPos);
    
    // Footer
    const footerY = pageHeight - 20;
    doc.setFontSize(9);
    doc.setTextColor(120, 120, 120);
    doc.text('Documento generato automaticamente dal sistema di gestione parcheggio', pageWidth / 2, footerY, { align: 'center' });
    doc.text(`Data generazione: ${new Date().toLocaleString('it-IT')}`, pageWidth / 2, footerY + 8, { align: 'center' });
    
    // Save the PDF
    const fileName = `Fattura_${billData.cliente_cognome}_${String(billData.mese).padStart(2, '0')}-${billData.anno}.pdf`;
    doc.save(fileName);
  };

  // Handle bill details modal
  const handleViewBillDetails = async (customerId, customerName) => {
    console.log('Dettagli clicked for:', customerId, customerName); // Debug log
    
    try {
      setIsLoading(true);
      
      console.log('Calling get_bill_details with:', { customerId, selectedMonth, selectedYear }); // Debug log
      
      // Get detailed bill data for the modal
      const { data: billDetails, error } = await supabase
        .rpc('get_bill_details', {
          customer_id: customerId,
          bill_year: selectedYear,
          bill_month: selectedMonth
        });

      console.log('Bill details result:', { billDetails, error }); // Debug log

      if (error) {
        console.error('Error fetching bill details:', error);
        alert(`Errore nel recupero dei dettagli della fattura: ${error.message}`);
        return;
      }

      if (!billDetails || billDetails.length === 0) {
        alert('Nessuna fattura trovata per questo cliente e periodo');
        return;
      }

      console.log('Setting bill details and showing modal'); // Debug log
      setSelectedBillDetails({
        ...billDetails[0],
        customerName: customerName
      });
      setShowBillDetails(true);
      
    } catch (error) {
      console.error('Error loading bill details:', error);
      alert(`Errore nel caricamento dei dettagli: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  // Mobile-friendly responsive chart
  const ChartContainer = ({ children }) => (
    <div className={`w-full ${isMobile ? 'h-64' : 'h-80'}`}>
      <ResponsiveContainer width="100%" height="100%">
        {children}
      </ResponsiveContainer>
    </div>
  );

  // Load data on component mount and when period OR customer selection changes
  useEffect(() => {
    loadDashboardData();
  }, [selectedMonth, selectedYear, selectedCustomer]);

  // Check admin permission
  if (!isAdmin) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <p className="text-lg text-slate-600 mb-2">Accesso Negato</p>
          <p className="text-slate-500">Solo gli amministratori possono accedere a questa sezione.</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`max-w-7xl mx-auto space-y-6 ${isMobile ? 'p-4' : 'p-6'}`}>
      {/* Page Header */}
      <div className="flex flex-col space-y-4">
        <div className="flex items-center space-x-3">
          <BarChart3 className="w-8 h-8 text-blue-600" />
          <h1 className={`${isMobile ? 'text-2xl' : 'text-3xl'} font-bold text-slate-800`}>
            Dashboard Finanziaria
          </h1>
        </div>
        <p className="text-slate-600">
          Gestione fatturazione e analisi dei ricavi del parcheggio
        </p>
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="flex items-center justify-center py-8">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
            <p className="text-slate-600">Caricamento dati...</p>
          </div>
        </div>
      )}

      {/* Revenue Chart */}
      <Card className="glass-effect border-0 shadow-lg">
        <CardHeader>
          <div className="flex flex-col space-y-4">
            <div className="flex items-center justify-between">
              <CardTitle className="text-xl font-semibold text-slate-800">
                Analisi Ricavi - Ultimi 12 Mesi
              </CardTitle>
              <BarChart3 className="w-6 h-6 text-blue-600" />
            </div>
            
            {/* Chart Filter */}
            <div className={`flex ${isMobile ? 'flex-col space-y-3' : 'items-center space-x-4'}`}>
              <div className="flex items-center space-x-2">
                <Filter className="w-4 h-4 text-slate-500" />
                <Label className="text-sm font-medium">Filtro Cliente:</Label>
              </div>
              <Select value={selectedCustomer} onValueChange={setSelectedCustomer}>
                <SelectTrigger className={`${isMobile ? 'w-full' : 'w-64'}`}>
                  <SelectValue placeholder="Tutti i clienti" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">Tutti i clienti</SelectItem>
                  {clienti.map((cliente) => (
                    <SelectItem key={cliente.id} value={cliente.id}>
                      {cliente.nome} {cliente.cognome}
                      {cliente.pricing_model === 'per_day' && ` (€${cliente.daily_rate}/giorno)`}
                      {cliente.pricing_model === 'monthly_flat' && ` (€${cliente.monthly_rate}/mese)`}
                      {cliente.pricing_model === 'no_cost' && ' (gratuito)'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {revenueData.length > 0 ? (
            <ChartContainer>
              <BarChart data={revenueData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip 
                  formatter={(value, name, props) => {
                    const currentRevenue = parseFloat(value);
                    const currentIndex = revenueData.findIndex(item => 
                      item.month === props.payload.month && item.year === props.payload.year
                    );
                    
                    let growthText = "";
                    if (currentIndex > 0) {
                      const previousRevenue = parseFloat(revenueData[currentIndex - 1].revenue);
                      if (previousRevenue > 0) {
                        const growthPercent = ((currentRevenue - previousRevenue) / previousRevenue * 100);
                        const growthSign = growthPercent >= 0 ? "+" : "";
                        growthText = ` (${growthSign}${growthPercent.toFixed(1)}% vs mese precedente)`;
                      }
                    }
                    
                    return [formatCurrency(value) + growthText, "Ricavi"];
                  }}
                  labelFormatter={(label) => `Mese: ${label}`}
                />
                <Bar dataKey="revenue" fill="#3b82f6" />
              </BarChart>
            </ChartContainer>
          ) : (
            <div className="flex items-center justify-center h-64">
              <p className="text-slate-500">
                {selectedCustomer ? "Nessun dato di ricavo per il cliente selezionato" : "Nessun dato di ricavo disponibile"}
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Customer Billing Management */}
      <Card className="glass-effect border-0 shadow-lg">
        <CardHeader>
          <div className="flex flex-col space-y-4">
            <div className="flex items-center space-x-3">
              <CreditCard className="w-6 h-6 text-green-600" />
              <CardTitle className="text-xl font-semibold text-slate-800">
                Gestione Fatturazione Clienti
              </CardTitle>
            </div>
            
            {/* Search and filters */}
            <div className={`flex ${isMobile ? 'flex-col space-y-3' : 'items-center justify-between'}`}>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Cerca cliente..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className={`pl-10 ${isMobile ? 'w-full' : 'w-64'}`}
                />
              </div>
              
              <div className={`flex ${isMobile ? 'flex-col space-y-2' : 'items-center space-x-3'}`}>
                <div className="flex items-center space-x-2">
                  <Calendar className="w-4 h-4 text-slate-500" />
                  <Label className="text-sm font-medium">Periodo:</Label>
                </div>
                <div className="flex space-x-2">
                  <Select value={selectedMonth.toString()} onValueChange={(value) => setSelectedMonth(parseInt(value))}>
                    <SelectTrigger className={`${isMobile ? 'w-full' : 'w-32'}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {months.map((month) => (
                        <SelectItem key={month.value} value={month.value.toString()}>
                          {month.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select value={selectedYear.toString()} onValueChange={(value) => setSelectedYear(parseInt(value))}>
                    <SelectTrigger className={`${isMobile ? 'w-full' : 'w-24'}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {years.map((year) => (
                        <SelectItem key={year} value={year.toString()}>
                          {year}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Bulk Actions */}
          <div className={`mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200`}>
            <div className={`flex ${isMobile ? 'flex-col space-y-3' : 'items-center justify-between'}`}>
              <div>
                <h3 className="font-medium text-blue-900">Generazione Fatture</h3>
                <p className="text-sm text-blue-700">
                  Genera tutte le fatture per {months.find(m => m.value === selectedMonth)?.label} {selectedYear}
                </p>
              </div>
              <div className={`flex ${isMobile ? 'flex-col' : 'space-x-2'}`}>
                <Button
                  onClick={handleGenerateMonthlyBills}
                  disabled={isLoading}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  <Download className="w-4 h-4 mr-2" />
                  {isLoading ? 'Generando...' : 'Genera Fatture'}
                </Button>
              </div>
            </div>
          </div>

          {/* Customer List */}
          <div className="space-y-4">
            {filteredCustomers.length > 0 ? (
              filteredCustomers.map((customer) => (
                <div
                  key={customer.id}
                  className={`p-4 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors ${
                    customer.pricingModel === 'no_cost' ? 'opacity-60' : ''
                  }`}
                >
                  <div className={`flex ${isMobile ? 'flex-col space-y-3' : 'items-center justify-between'}`}>
                    <div className={`flex ${isMobile ? 'flex-col space-y-2' : 'items-center space-x-4'}`}>
                      <div>
                        <h3 className="font-semibold text-slate-800">{customer.customer}</h3>
                        <div className="flex items-center space-x-4 mt-1">
                          <span className="text-sm text-slate-600">
                            {getPricingModelText(customer.pricingModel, customer.rate)}
                          </span>
                          <span className="text-sm text-slate-500">
                            {customer.activeCars} auto attive
                          </span>
                        </div>
                      </div>
                      <div className={`${isMobile ? 'self-start' : ''}`}>
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                          customer.status === 'generated' 
                            ? 'bg-green-100 text-green-800'
                            : customer.status === 'no_bill'
                            ? 'bg-gray-100 text-gray-600'
                            : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {customer.status === 'generated' ? 'Fattura Generata' : 
                           customer.status === 'no_bill' ? 'Nessuna Fattura' : 
                           'In Sospeso'}
                        </span>
                      </div>
                    </div>
                    
                    <div className={`flex ${isMobile ? 'flex-col space-y-2' : 'items-center space-x-4'}`}>
                      {customer.pricingModel !== 'no_cost' && (
                        <div className={`text-right ${isMobile ? 'text-left' : ''}`}>
                          <div className="text-lg font-semibold text-slate-800">
                            {formatCurrency(customer.monthlyAmount)}
                          </div>
                          <div className="text-xs text-slate-500">
                            Importo mensile
                          </div>
                        </div>
                      )}
                      
                        {/* Instead of using isMobile conditions, use responsive classes */}
                        <div className="flex flex-col space-y-2 w-full md:flex-row md:space-y-0 md:space-x-2 md:w-auto">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleGeneratePDF(customer.id, selectedMonth, selectedYear)}
                            disabled={isLoading || customer.pricingModel === 'no_cost'}
                            className="w-full md:w-auto"
                        >
                            <FileText className="w-4 h-4 mr-1" />
                            PDF
                        </Button>
                        <Button
                            variant="outline"
                            size="sm"
                            className="w-full md:w-auto"
                            onClick={() => handleViewBillDetails(customer.id, customer.customer)}
                        >
                            Dettagli
                        </Button>
                        </div>

                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-12">
                <p className="text-slate-500">
                  {customerBills.length === 0 
                    ? "Nessuna fattura generata ancora. Genera le prime fatture utilizzando le funzioni SQL fornite." 
                    : "Nessun cliente trovato con i filtri correnti."}
                </p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <ConfirmationModal
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={executeGenerateMonthlyBills}
        title="Conferma Generazione"
        message={`Sei sicuro di voler generare le fatture per ${months.find(m => m.value === selectedMonth)?.label} ${selectedYear}?\n\nQuesta operazione creerà le fatture per tutti i clienti con auto parcheggiate in questo periodo.`}
        confirmText="Genera Fatture"
        isLoading={isLoading}
        />

        <ResultModal
        isOpen={showResultModal}
        onClose={() => setShowResultModal(false)}
        success={resultData.success}
        title={resultData.success ? 'Generazione Completata' : 'Errore Generazione'}
        data={resultData.data}
        />

        <BillDetailsModal
        isOpen={showBillDetails}
        onClose={() => setShowBillDetails(false)}
        billDetails={selectedBillDetails}
        onGeneratePDF={() => generateBillPDF(selectedBillDetails)}  // Direct PDF generation
        isLoading={isLoading}
        />
    </div>
  );
}